// Message database with Firebase persistence
import { Notification, NotificationType, Message, Conversation } from '../models/Notification';
import { logMarketplaceActivity } from './adminActivityDatabase.ts';
import { getTrackingInfo } from '../utils/trackingUtils.ts';
import { getUserById, getDisplayName } from './userDatabase.ts';
import { db } from '../config/firebase.ts';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { sendNotificationEmail } from '../utils/emailUtils';
import { User } from '../models/User'; // Added import for User

// Default mock data
const DEFAULT_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    userId: '1',
    title: 'New Match Found',
    message: 'Your buy request for 100 shares at $45.50 matches a sale offer by Håvar Bauck.',
    timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    isRead: false,
    type: NotificationType.MATCH_FOUND,
    relatedEntityId: '123',
    actionUrl: '/marketplace'
  },
  {
    id: '2',
    userId: '2',
    title: 'New Message',
    message: 'You have received a new message from Endre Opdal.',
    timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    isRead: true,
    type: NotificationType.MESSAGE_RECEIVED,
    relatedEntityId: '456',
    actionUrl: '/inbox'
  }
];

const DEFAULT_MESSAGES: Message[] = [
  {
    id: '1',
    senderId: '1',
    recipientId: '2',
    content: 'Hello, I noticed your buy request matches my selling price. Would you like to discuss further?',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    isRead: false,
    relatedEntityId: '123'
  },
  {
    id: '2',
    senderId: '2',
    recipientId: '1',
    content: 'I am interested in your share offer. Is the price negotiable?',
    timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    isRead: false,
    relatedEntityId: '456'
  }
];

const DEFAULT_CONVERSATIONS: Conversation[] = [
  {
    id: '1',
    participants: ['1', '2'],
    messages: [
      {
        id: '1',
        senderId: '1',
        recipientId: '2',
        content: 'Hello, I noticed your buy request matches my selling price. Would you like to discuss further?',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        isRead: false,
        relatedEntityId: '123'
      },
      {
        id: '2',
        senderId: '2',
        recipientId: '1',
        content: 'I am interested in your share offer. Is the price negotiable?',
        timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        isRead: false,
        relatedEntityId: '456'
      }
    ],
    lastMessageTimestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
  }
];

// Initialize data from Firestore or defaults
let MESSAGES: Message[] = [];
let CONVERSATIONS: Conversation[] = [];
let NOTIFICATIONS: Notification[] = [];

// Initialize Firestore collections
export const initializeMessageDatabase = async (): Promise<void> => {
  try {
    // Load messages
    const messagesQuery = query(collection(db, 'messages'), orderBy('timestamp', 'desc'));
    const messagesSnapshot = await getDocs(messagesQuery);
    
    if (messagesSnapshot.empty) {
      // Initialize with defaults if empty
      const batch = writeBatch(db);
      DEFAULT_MESSAGES.forEach(message => {
        const docRef = doc(db, 'messages', message.id);
        batch.set(docRef, message);
      });
      await batch.commit();
      MESSAGES = [...DEFAULT_MESSAGES];
    } else {
      MESSAGES = messagesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Message));
    }

    // Load conversations
    const conversationsQuery = query(collection(db, 'conversations'), orderBy('lastMessageTimestamp', 'desc'));
    const conversationsSnapshot = await getDocs(conversationsQuery);
    
    if (conversationsSnapshot.empty) {
      // Initialize with defaults if empty
      const batch = writeBatch(db);
      DEFAULT_CONVERSATIONS.forEach(conversation => {
        const docRef = doc(db, 'conversations', conversation.id);
        batch.set(docRef, conversation);
      });
      await batch.commit();
      CONVERSATIONS = [...DEFAULT_CONVERSATIONS];
    } else {
      CONVERSATIONS = conversationsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Conversation));
    }

    // Load notifications
    const notificationsQuery = query(collection(db, 'notifications'), orderBy('timestamp', 'desc'));
    const notificationsSnapshot = await getDocs(notificationsQuery);
    
    if (notificationsSnapshot.empty) {
      // Initialize with defaults if empty
      const batch = writeBatch(db);
      DEFAULT_NOTIFICATIONS.forEach(notification => {
        const docRef = doc(db, 'notifications', notification.id);
        batch.set(docRef, notification);
      });
      await batch.commit();
      NOTIFICATIONS = [...DEFAULT_NOTIFICATIONS];
    } else {
      NOTIFICATIONS = notificationsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Notification));
    }
  } catch (error) {
    console.error('Failed to initialize message database:', error);
    // Fallback to defaults
    MESSAGES = [...DEFAULT_MESSAGES];
    CONVERSATIONS = [...DEFAULT_CONVERSATIONS];
    NOTIFICATIONS = [...DEFAULT_NOTIFICATIONS];
  }
};

// Message functions
export const getAllMessages = async (): Promise<Message[]> => {
  try {
    const messagesQuery = query(collection(db, 'messages'), orderBy('timestamp', 'desc'));
    const messagesSnapshot = await getDocs(messagesQuery);
    return messagesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Message));
  } catch (error) {
    console.error('Failed to get all messages:', error);
    return [];
  }
};

export const getMessagesForUser = async (userId: string): Promise<Message[]> => {
  try {
    const messagesQuery = query(
      collection(db, 'messages'),
      where('recipientId', '==', userId),
      orderBy('timestamp', 'desc')
    );
    const messagesSnapshot = await getDocs(messagesQuery);
    return messagesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Message));
  } catch (error) {
    console.error('Failed to get messages for user:', error);
    return [];
  }
};

export const addMessage = async (message: Message): Promise<void> => {
  try {
    const messageRef = doc(db, 'messages', message.id);
    await setDoc(messageRef, message);
  } catch (error) {
    console.error('Failed to add message:', error);
    throw error;
  }
};

export const markMessageAsRead = async (messageId: string): Promise<void> => {
  try {
    const messageRef = doc(db, 'messages', messageId);
    await updateDoc(messageRef, { isRead: true });
  } catch (error) {
    console.error('Failed to mark message as read:', error);
    throw error;
  }
};

export const markAllMessagesAsRead = async (userId: string): Promise<void> => {
  try {
    const messagesQuery = query(
      collection(db, 'messages'),
      where('recipientId', '==', userId),
      where('isRead', '==', false)
    );
    const messagesSnapshot = await getDocs(messagesQuery);
    
    const batch = writeBatch(db);
    messagesSnapshot.docs.forEach(doc => {
      batch.update(doc.ref, { isRead: true });
    });
    await batch.commit();
  } catch (error) {
    console.error('Failed to mark all messages as read:', error);
    throw error;
  }
};

// Conversation functions
export const getAllConversations = async (): Promise<Conversation[]> => {
  try {
    const conversationsQuery = query(collection(db, 'conversations'), orderBy('lastMessageTimestamp', 'desc'));
    const conversationsSnapshot = await getDocs(conversationsQuery);
    return conversationsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Conversation));
  } catch (error) {
    console.error('Failed to get all conversations:', error);
    return [];
  }
};

export const getConversationsForUser = async (userId: string): Promise<Conversation[]> => {
  try {
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      orderBy('lastMessageTimestamp', 'desc')
    );
    const conversationsSnapshot = await getDocs(conversationsQuery);
    return conversationsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Conversation));
  } catch (error) {
    console.error('Failed to get conversations for user:', error);
    return [];
  }
};

export const addConversation = async (conversation: Conversation): Promise<void> => {
  try {
    const conversationRef = doc(db, 'conversations', conversation.id);
    await setDoc(conversationRef, conversation);
  } catch (error) {
    console.error('Failed to add conversation:', error);
    throw error;
  }
};

export const updateConversation = async (conversationId: string, updatedConversation: Conversation): Promise<void> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await setDoc(conversationRef, updatedConversation);
  } catch (error) {
    console.error('Failed to update conversation:', error);
    throw error;
  }
};

export const addMessageToConversation = async (conversationId: string, message: Message): Promise<void> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (conversationDoc.exists()) {
      const conversation = conversationDoc.data() as Conversation;
      const updatedMessages = [...conversation.messages, message];
      
      await updateDoc(conversationRef, {
        messages: updatedMessages,
        lastMessageTimestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Failed to add message to conversation:', error);
    throw error;
  }
};

export const findOrCreateConversation = async (userId1: string, userId2: string): Promise<Conversation> => {
  try {
    // Check for existing conversation
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId1)
    );
    const conversationsSnapshot = await getDocs(conversationsQuery);
    
    const existingConversation = conversationsSnapshot.docs.find(doc => {
      const conversation = doc.data() as Conversation;
      return conversation.participants.includes(userId2);
    });
    
    if (existingConversation) {
      return { ...existingConversation.data(), id: existingConversation.id } as Conversation;
    }
    
    // Create new conversation
    const newConversation: Conversation = {
      id: `conversation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      participants: [userId1, userId2],
      messages: [],
      lastMessageTimestamp: new Date().toISOString()
    };
    
    await addConversation(newConversation);
    return newConversation;
  } catch (error) {
    console.error('Failed to find or create conversation:', error);
    throw error;
  }
};

// Notification functions
export const getAllNotifications = async (): Promise<Notification[]> => {
  try {
    const notificationsQuery = query(collection(db, 'notifications'), orderBy('timestamp', 'desc'));
    const notificationsSnapshot = await getDocs(notificationsQuery);
    return notificationsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Notification));
  } catch (error) {
    console.error('Failed to get all notifications:', error);
    return [];
  }
};

export const getNotificationsForUser = async (userId: string): Promise<Notification[]> => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc')
    );
    const notificationsSnapshot = await getDocs(notificationsQuery);
    return notificationsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Notification));
  } catch (error) {
    console.error('Failed to get notifications for user:', error);
    return [];
  }
};

export const addNotification = async (notification: Notification, senderId?: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notification.id);
    await setDoc(notificationRef, notification);

    // Send email notification if user has an email
    const user = await getUserById(notification.userId);
    if (user && user.email) {
      await sendNotificationEmail(
        user,
        notification.title,
        notification.message,
        notification.actionUrl,
        senderId || 'system' // Use 'system' as default if no senderId provided
      );
    }
  } catch (error) {
    console.error('Failed to add notification:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, { isRead: true });
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isRead', '==', false)
    );
    const notificationsSnapshot = await getDocs(notificationsQuery);
    
    const batch = writeBatch(db);
    notificationsSnapshot.docs.forEach(doc => {
      batch.update(doc.ref, { isRead: true });
    });
    await batch.commit();
  } catch (error) {
    console.error('Failed to mark all notifications as read:', error);
    throw error;
  }
};

export const getUnreadMessageCount = async (userId: string): Promise<number> => {
  try {
    const messagesQuery = query(
      collection(db, 'messages'),
      where('recipientId', '==', userId),
      where('isRead', '==', false)
    );
    const messagesSnapshot = await getDocs(messagesQuery);
    return messagesSnapshot.size;
  } catch (error) {
    console.error('Failed to get unread message count:', error);
    return 0;
  }
};

export const markConversationAsRead = async (conversationId: string, userId: string): Promise<void> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (conversationDoc.exists()) {
      const conversation = conversationDoc.data() as Conversation;
      const updatedMessages = conversation.messages.map(message => 
        message.recipientId === userId ? { ...message, isRead: true } : message
      );
      
      await updateDoc(conversationRef, { messages: updatedMessages });
    }
  } catch (error) {
    console.error('Failed to mark conversation as read:', error);
    throw error;
  }
};

export const sendMessage = async (
  senderId: string,
  recipientId: string,
  content: string,
  relatedEntityId?: string,
  senderData?: User // Optional: pass sender data to avoid database lookup
): Promise<Message> => {
  try {
    // Validate inputs
    if (!senderId) {
      throw new Error('Sender ID is required');
    }
    if (!recipientId) {
      throw new Error('Recipient ID is required');
    }
    if (!content.trim()) {
      throw new Error('Message content is required');
    }

    // Create new message
    const messageId = `message_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newMessage: Message = {
      id: messageId,
      senderId,
      recipientId,
      content,
      timestamp: new Date().toISOString(),
      isRead: false
    };
    if (relatedEntityId) {
      newMessage.relatedEntityId = relatedEntityId;
    }

    // Start parallel operations for the core message sending
    const [conversation, sender] = await Promise.all([
      findOrCreateConversation(senderId, recipientId),
      senderData ? Promise.resolve(senderData) : getUserById(senderId)
    ]);

    if (!sender) {
      throw new Error(`Sender not found with ID: ${senderId}`);
    }

    // Execute core message operations using batch writes for better performance
    const batch = writeBatch(db);
    
    // Add message to messages collection
    const messageRef = doc(db, 'messages', newMessage.id);
    batch.set(messageRef, newMessage);
    
    // Update conversation with new message
    const conversationRef = doc(db, 'conversations', conversation.id);
    const updatedConversation = {
      ...conversation,
      messages: [...conversation.messages, newMessage],
      lastMessageTimestamp: newMessage.timestamp
    };
    batch.set(conversationRef, updatedConversation);
    
    // Execute batch write
    await batch.commit();

    // Create notification (but don't wait for it)
    const notification: Notification = {
      id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: recipientId,
      title: 'New Message',
      message: `You have received a new message from ${getDisplayName(sender)}.`,
      timestamp: new Date().toISOString(),
      isRead: false,
      type: NotificationType.MESSAGE_RECEIVED,
      relatedEntityId: conversation.id,
      actionUrl: '/inbox'
    };

    // Add notification as a non-blocking operation
    const notificationPromise = addNotification(notification, senderId).catch(error => {
      console.warn('Failed to add notification (non-blocking):', error);
    });

    // Execute core operations
    // await Promise.all(coreOperations); // This line is no longer needed as batch writes are used

    // Handle marketplace activity logging asynchronously (non-blocking)
    if (relatedEntityId) {
      const marketplaceActivityPromise = (async () => {
        try {
          const recipient = await getUserById(recipientId);
          if (!recipient) {
            console.warn(`Recipient not found with ID: ${recipientId}, skipping marketplace activity log`);
            return;
          }

          const isBuyRequest = relatedEntityId.includes('buy_request');
          const action = isBuyRequest ? 'contact_buyer' : 'contact_seller';
          const entityType = isBuyRequest ? 'buy_request' : 'sale_offer';

          await logMarketplaceActivity(
            senderId,
            getDisplayName(sender),
            action,
            relatedEntityId,
            entityType,
            {
              contactedUserId: recipientId,
              contactedUserName: getDisplayName(recipient)
            }
          );
        } catch (activityError) {
          console.warn('Failed to log marketplace activity (non-blocking):', activityError);
        }
      })();

      // Don't wait for these non-blocking operations
      Promise.all([notificationPromise, marketplaceActivityPromise]).catch(() => {
        // Silently handle errors for non-blocking operations
      });
    } else {
      // Just handle notification for non-marketplace messages
      notificationPromise.catch(() => {
        // Silently handle notification errors
      });
    }

    return newMessage;
  } catch (error) {
    console.error('Failed to send message:', error);
    throw error;
  }
};

export const resetMessagesToDefaults = async (): Promise<void> => {
  try {
    // Delete all existing data
    const collections = ['messages', 'conversations', 'notifications'];
    const batch = writeBatch(db);
    
    for (const collectionName of collections) {
      const querySnapshot = await getDocs(collection(db, collectionName));
      querySnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
    }
    await batch.commit();
    
    // Initialize with defaults
    await initializeMessageDatabase();
  } catch (error) {
    console.error('Failed to reset messages to defaults:', error);
    throw error;
  }
}; 